/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Pending Invitations Panel Component
 * Displays and manages pending family invitations
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState } from 'react'
import { Mail, Copy, Check, X, Clock, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('PendingInvitationsPanel')

const PendingInvitationsPanel = ({ invitations, onInvitationCancelled }) => {
  const [copied, setCopied] = useState(null)
  const [cancelling, setCancelling] = useState(null)

  if (!invitations || invitations.length === 0) {
    return null
  }

  const handleCopyLink = async (token) => {
    const appUrl = window.location.origin
    const link = `${appUrl}/register/student?token=${token}`

    try {
      await navigator.clipboard.writeText(link)
      setCopied(token)
      toast.success('Link copied to clipboard!')

      setTimeout(() => {
        setCopied(null)
      }, 2000)
    } catch (error) {
      logger.error('Failed to copy:', error)
      toast.error('Failed to copy link')
    }
  }

  const handleCancelInvitation = async (invitationId, email) => {
    if (!confirm(`Cancel invitation for ${email}?`)) {
      return
    }

    setCancelling(invitationId)

    try {
      await api.delete(`/invitations/${invitationId}`)
      toast.success('Invitation cancelled')

      if (onInvitationCancelled) {
        onInvitationCancelled(invitationId)
      }
    } catch (error) {
      logger.error('Error cancelling invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to cancel invitation')
    } finally {
      setCancelling(null)
    }
  }

  const getTimeRemaining = (expiresAt) => {
    const now = new Date()
    const expiry = new Date(expiresAt)
    const diff = expiry - now

    if (diff <= 0) {
      return 'Expired'
    }

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} left`
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} left`
    }
    return 'Less than 1 hour'
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Pending Invitations
        </h3>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          {invitations.map((invitation) => {
            const isExpired = new Date(invitation.expiresAt) <= new Date()
            const isCopied = copied === invitation.invitationToken

            return (
              <div
                key={invitation.id}
                className={`p-4 rounded-lg border ${
                  isExpired
                    ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
                    : 'border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left side - Email and info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail
                        className={`w-4 h-4 flex-shrink-0 ${isExpired ? 'text-gray-400' : 'text-primary-600 dark:text-primary-400'}`}
                      />
                      <span
                        className={`font-medium truncate ${isExpired ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
                      >
                        {invitation.email}
                      </span>
                    </div>

                    {invitation.metadata?.firstName && invitation.metadata?.lastName && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {invitation.metadata.firstName} {invitation.metadata.lastName}
                      </p>
                    )}

                    <div className="flex items-center gap-2 text-xs">
                      <Clock
                        className={`w-3 h-3 ${isExpired ? 'text-error-500' : 'text-gray-500'}`}
                      />
                      <span
                        className={
                          isExpired
                            ? 'text-error-600 dark:text-error-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }
                      >
                        {isExpired ? 'Expired' : getTimeRemaining(invitation.expiresAt)}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 dark:text-gray-400">
                        Sent {new Date(invitation.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Right side - Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!isExpired && (
                      <button
                        onClick={() => handleCopyLink(invitation.invitationToken)}
                        className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        title="Copy invitation link"
                      >
                        {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}

                    <button
                      onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                      disabled={cancelling === invitation.id}
                      className="p-2 text-error-600 dark:text-error-400 hover:bg-error-100 dark:hover:bg-error-900/30 rounded-lg transition-colors disabled:opacity-50"
                      title="Cancel invitation"
                    >
                      {cancelling === invitation.id ? (
                        <div className="w-4 h-4 border-2 border-error-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default PendingInvitationsPanel
